import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { format, getYear, isValid, parseISO, subYears } from 'date-fns';
import type { MaritimeIncident } from '../../models/maritime-incidents/maritime-incident.model';

export interface MaritimeIncidentFilters {
  incidentTypes: string[];
  startDate: string;
  endDate: string;
}

interface MaritimeIncidentsState {
  selectedIncident: MaritimeIncident | null;
  filters: MaritimeIncidentFilters;
  incidents: MaritimeIncident[];
  allIncidentTypes: string[];
  dataYearMin: number;
  dataLoaded: boolean;
  dataLoading: boolean;
  dataError: boolean;
}

const FALLBACK_YEAR_MIN = 2019;

/*** Returns the YYYY-MM-DD string for the current date minus the input years */
function getRelativeDateString(yearsOffset: number = 0): string {
  const date = subYears(new Date(), yearsOffset);
  return format(date, 'yyyy-MM-dd');
}

function defaultFilters(allIncidentTypes: string[]): MaritimeIncidentFilters {
  return {
    incidentTypes: allIncidentTypes,
    startDate: getRelativeDateString(1),
    endDate: getRelativeDateString(0),
  };
}

const initialState: MaritimeIncidentsState = {
  selectedIncident: null,
  filters: defaultFilters([]),
  incidents: [],
  allIncidentTypes: [],
  dataYearMin: FALLBACK_YEAR_MIN,
  dataLoaded: false,
  dataLoading: false,
  dataError: false,
};

function getMinYear(incident: MaritimeIncident): number | null {
  const dateStr = incident.incidentDate;
  if (!dateStr) return null;
  const date = parseISO(dateStr);
  return isValid(date) ? getYear(date) : null;
}

const maritimeIncidentsSlice = createSlice({
  name: 'maritimeIncidents',
  initialState,
  reducers: {
    setMaritimeIncidentLoading: (state) => {
      state.dataLoading = true;
      state.dataError = false;
    },
    setMaritimeIncidentError: (state) => {
      state.dataLoading = false;
      state.dataError = true;
    },
    /**
     * Called once data arrives from the API.
     * Filters out Rejected records, derives types and year range,
     * and resets filters to show everything.
     */
    setMaritimeIncidentData: (
      state,
      action: PayloadAction<MaritimeIncident[]>
    ) => {
      const approved = action.payload.filter(
        (incident) =>
          (incident.analystReview ?? '').toLowerCase() === 'approved'
      );

      const types = Array.from(
        new Set(
          approved.map(
            (incident) => (incident.incidentType ?? 'Unknown') as string
          )
        )
      ).sort();

      const years = approved
        .map(getMinYear)
        .filter((y): y is number => y !== null);

      state.incidents = approved;
      state.allIncidentTypes = types;
      state.dataYearMin =
        years.length > 0 ? Math.min(...years) : FALLBACK_YEAR_MIN;
      state.dataLoaded = true;
      state.dataLoading = false;
      state.dataError = false;
      state.filters = defaultFilters(types);
    },
    setSelectedMaritimeIncident: (
      state,
      action: PayloadAction<MaritimeIncident | null>
    ) => {
      state.selectedIncident = action.payload;
    },
    setMaritimeIncidentFilters: (
      state,
      action: PayloadAction<Partial<MaritimeIncidentFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetMaritimeIncidentFilters: (state) => {
      state.filters = defaultFilters(state.allIncidentTypes);
    },
  },
});

export const {
  setMaritimeIncidentLoading,
  setMaritimeIncidentError,
  setMaritimeIncidentData,
  setSelectedMaritimeIncident,
  setMaritimeIncidentFilters,
  resetMaritimeIncidentFilters,
} = maritimeIncidentsSlice.actions;
export default maritimeIncidentsSlice.reducer;
